/**
 * @file Dense Recalls month table rendering and filtering helpers.
 */

import { appData } from "../../app/state/state.js";
import {
    NATIVE_CHECKBOX_CLASSES,
    NATIVE_CHECKBOX_ICON_CLASSES,
    NATIVE_CHECKBOX_LABEL_CLASSES,
    NATIVE_CHECKBOX_MARKER_CLASSES,
} from "../../shared/forms/nativeFieldStyles.js";
import {
    convertIdsToNamesForEditing,
    escapeHTML,
    formatAndSanitizeUserContent,
} from "../../shared/utils/contentUtils.js";
import { formatDate } from "../../shared/utils/dateUtils.js";
import { isColorDark } from "../../shared/utils/displayUtils.js";
import {
    getSelectedRecallsExportIds,
    recallsRouteState,
} from "./recallsState.js";
import { buildRecallsMonthBucketModel } from "./recallsMonthBuckets.js";
import {
    renderRecallStatusInlineControl,
} from "./recallsInlineEditing.js";
import {
    getRecallReasonDisplay,
    normalizeRecallReasonIds,
} from "./recallsReasonUtils.js";
import {
    getRecallProviderDisplay as getProviderDisplay,
} from "./recallsProviderUtils.js";
import { formatRecallsSystemNoteContent } from "./recallsSystemNoteContent.js";

const SORT_OPTIONS = Object.freeze([
    { value: "recall_date", label: "Recall date" },
    { value: "patient", label: "Name" },
    { value: "reason", label: "Reason" },
    { value: "provider", label: "Provider" },
    { value: "status", label: "Status" },
    { value: "processed", label: "Emailed" },
]);

const PROCESSED_FILTERS = Object.freeze([
    { value: "all", label: "All rows" },
    { value: "processed", label: "Emailed" },
    { value: "unprocessed", label: "Not emailed" },
]);
const TABLE_COLUMN_COUNT = 9;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RECALLS_CONTROL_BUTTON_CLASS = [
    "relative inline-flex h-10 w-10 items-center justify-center rounded-full",
    "border bg-white shadow-sm transition-colors",
    "hover:border-[var(--dfc-brand-primary)] hover:text-[var(--dfc-brand-primary)]",
].join(" ");
const RECALLS_CONTROL_BUTTON_INACTIVE_CLASS = "border-gray-200 text-gray-500";
const RECALLS_CONTROL_BUTTON_ACTIVE_CLASS = "border-[var(--dfc-brand-accent)] text-[var(--dfc-brand-accent)]";
const RECALLS_CONTROL_MENU_CLASS = [
    "absolute right-0 top-12 z-20 rounded-2xl border border-gray-200",
    "bg-white p-3 shadow-xl",
].join(" ");
const MENU_ROW_CLASSES = [
    "flex w-full items-center rounded-xl px-3 py-2 text-left text-sm",
    "transition-colors",
].join(" ");
const DEFAULT_RECALLS_SORT_FIELD = "status";
const DEFAULT_RECALLS_SORT_DIRECTION = "asc";
const DEFAULT_RECALLS_SORT_CHAIN = Object.freeze([
    "status",
    "closed_at_recent",
    "recall_date",
    "patient",
    "updated_at",
]);
const SORT_ORDER_FALLBACK = 9999;

function attrValue(value) {
    return escapeHTML(String(value ?? ""));
}

function checkedAttr(checked) {
    return checked ? " checked" : "";
}

function disabledAttr(disabled) {
    return disabled ? " disabled" : "";
}

function renderCheckboxControl({
    ariaLabel,
    checked = false,
    disabled = false,
    inputAttrs = "",
    label = "",
    labelClassName = "",
    markerClassName = "",
}) {
    const labelClasses = [
        NATIVE_CHECKBOX_LABEL_CLASSES,
        labelClassName,
        disabled ? "opacity-60" : "",
    ].filter(Boolean).join(" ");
    const markerClasses = [
        NATIVE_CHECKBOX_MARKER_CLASSES,
        markerClassName,
    ].filter(Boolean).join(" ");
    return `
        <label class="${labelClasses}" data-native-checkbox-label>
            <input
                type="checkbox"
                class="${NATIVE_CHECKBOX_CLASSES}"
                ${inputAttrs}
                ${checkedAttr(checked)}
                ${disabledAttr(disabled)}
                ${ariaLabel ? `aria-label="${attrValue(ariaLabel)}"` : ""}
            >
            <span class="${markerClasses}" data-native-checkbox-marker>
                <span class="${NATIVE_CHECKBOX_ICON_CLASSES} ${checked ? "" : "opacity-0"}" data-native-checkbox-icon>check</span>
            </span>
            ${label ? `<span>${escapeHTML(label)}</span>` : ""}
        </label>
    `;
}

function getMonthKey(date = new Date()) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
}

function addMonths(monthKey, offset) {
    const [year, month] = /^\d{4}-\d{2}$/.test(monthKey || "") ?
        monthKey.split("-").map(Number) :
        [new Date().getFullYear(), new Date().getMonth() + 1];
    return getMonthKey(new Date(year, month - 1 + offset, 1));
}

function isValidMonthKey(monthKey) {
    return /^\d{4}-\d{2}$/.test(monthKey || "");
}

function shouldMonthRenderExpanded(
    monthKey,
    { hasActiveRows = false, isClosedOnly = false } = {},
) {
    if ((recallsRouteState.collapsedMonths || []).includes(monthKey)) {
        return false;
    }
    if ((recallsRouteState.expandedMonths || []).includes(monthKey)) {
        return true;
    }
    const currentMonth = getMonthKey();
    return !isClosedOnly && isValidMonthKey(monthKey) && (
        monthKey === currentMonth ||
        (monthKey < currentMonth && hasActiveRows === true)
    );
}

function getMonthLabel(monthKey) {
    if (!/^\d{4}-\d{2}$/.test(monthKey || "")) {
        return "Unscheduled";
    }
    const [year, month] = monthKey.split("-").map(Number);
    return new Intl.DateTimeFormat("en-AU", {
        month: "long",
        year: "numeric",
    }).format(new Date(year, month - 1, 1));
}

function getEffectiveMonth(item) {
    if (item?.__recallDraft === true) {return "drafts";}
    if (item?.effectiveMonth) {return item.effectiveMonth;}
    const dateValue = item?.email_date || item?.recall_date || "";
    return /^\d{4}-\d{2}/.test(dateValue) ? dateValue.slice(0, 7) : "none";
}

function getPatientName(item) {
    return [item?.first_name, item?.last_name].filter(Boolean).join(" ");
}

function getStatusLabel(statusId) {
    const status = (appData.config?.recallStatus || [])
        .find((option) => option?.id === statusId);
    return status?.name || statusId || "Unassigned";
}

function getStatusConfig(statusId) {
    return (appData.config?.recallStatus || [])
        .find((option) => option?.id === statusId) || null;
}

function getAdminOptionOrder(option, index = SORT_ORDER_FALLBACK) {
    const order = Number(option?.order);
    return Number.isFinite(order) ? order : index + 1;
}

function getAdminOrderedOptionEntries(options = []) {
    return (Array.isArray(options) ? options : [])
        .map((option, index) => ({
            index,
            option,
            order: getAdminOptionOrder(option, index),
        }))
        .sort((entryA, entryB) => {
            const orderDiff = entryA.order - entryB.order;
            return orderDiff !== 0 ? orderDiff : entryA.index - entryB.index;
        });
}

function isOptionActive(option) {
    return option?.isActive !== false && option?.active !== false;
}

function getStatusSortParts(statusId) {
    const statuses = appData.config?.recallStatus || [];
    const index = statuses.findIndex((option) => option?.id === statusId);
    const status = index >= 0 ? statuses[index] : null;
    return {
        label: getStatusLabel(statusId).toLowerCase(),
        order: status ? getAdminOptionOrder(status, index) : SORT_ORDER_FALLBACK,
        value: String(statusId || ""),
    };
}

function getReasonSortParts(reasonIds) {
    const ids = normalizeRecallReasonIds(reasonIds);
    const reasons = appData.config?.recalls?.recallReasons || [];
    const reasonOrderMap = new Map(reasons.map((reason, index) => [
        reason?.id,
        getAdminOptionOrder(reason, index),
    ]));
    const orderedReasonValues = ids.map((reasonId) => ({
        id: reasonId,
        order: reasonOrderMap.get(reasonId) ?? SORT_ORDER_FALLBACK,
    }));
    orderedReasonValues.sort((reasonA, reasonB) => {
        const orderDiff = reasonA.order - reasonB.order;
        return orderDiff !== 0 ?
            orderDiff :
            reasonA.id.localeCompare(reasonB.id);
    });
    return {
        label: getRecallReasonLabel(reasonIds).toLowerCase(),
        order: orderedReasonValues[0]?.order ?? SORT_ORDER_FALLBACK,
        value: orderedReasonValues.map((reason) => reason.id).join("|"),
    };
}

function normalizeHexColor(value) {
    const normalized = String(value || "").trim();
    const shorthandMatch = normalized.match(/^#([a-f0-9]{3})$/i);
    if (shorthandMatch) {
        return `#${shorthandMatch[1]
            .split("")
            .map((char) => `${char}${char}`)
            .join("")}`;
    }
    return /^#[a-f0-9]{6}$/i.test(normalized) ? normalized : "";
}

function getStatusRowPresentation(statusId) {
    const rawColor = String(getStatusConfig(statusId)?.color || "").trim();
    const lowerColor = rawColor.toLowerCase();
    if (
        !rawColor ||
        lowerColor === "transparent" ||
        lowerColor === "none" ||
        lowerColor === "rgba(0, 0, 0, 0)" ||
        lowerColor === "rgba(0,0,0,0)"
    ) {
        return { className: "", style: "" };
    }

    const hexColor = normalizeHexColor(rawColor);
    if (!hexColor || hexColor === "#ffffff") {
        return { className: "", style: "" };
    }
    const red = parseInt(hexColor.slice(1, 3), 16);
    const green = parseInt(hexColor.slice(3, 5), 16);
    const blue = parseInt(hexColor.slice(5, 7), 16);
    if (red > 248 && green > 248 && blue > 248) {
        return { className: "", style: "" };
    }
    const textColor = isColorDark(hexColor) ? "#ffffff" : "#111827";
    return {
        className: "recalls-contrast-row",
        style: `background-color: ${hexColor}; color: ${textColor};`,
    };
}

function getRecallReasonLabel(reasonIds) {
    return getRecallReasonDisplay(
        reasonIds,
        appData.config?.recalls?.recallReasons || [],
    );
}

function getLoadedRecallsRows() {
    if (recallsRouteState.advancedSearch?.active === true) {
        return recallsRouteState.advancedSearch.results || [];
    }
    return [
        ...(appData.recalls || []),
        ...(recallsRouteState.showClosed ? (appData.closedRecalls || []) : []),
    ];
}

function getLatestNoteText(item) {
    if (item?.latestNote?.isSystemNote === true) {return "";}
    return formatRecallsSystemNoteContent(item?.latestNote?.text || "").trim();
}

function hasMultipleEmailValues(value) {
    const normalized = String(value || "").trim();
    if (!normalized) {return false;}
    if ((normalized.match(/@/g) || []).length > 1) {return true;}
    return /[,;]/.test(normalized) || /\s/.test(normalized);
}

function hasExportableEmail(item) {
    const email = String(item?.email || "").trim();
    if (!email || item?.emailStatus === "no_email") {return false;}
    if (hasMultipleEmailValues(email)) {return false;}
    return EMAIL_PATTERN.test(email);
}

/**
 * Returns whether a Recall row can be selected for export.
 * @param {object} item Recall row.
 * @returns {boolean} True when row is selectable.
 */
export function isRecallExportSelectable(item) {
    return Boolean(
        item?.id &&
        item.__recallDraft !== true &&
        item.isClosedResolved !== true &&
        item.exportEligible !== false &&
        hasExportableEmail(item),
    );
}

function renderLatestNoteCell(item) {
    const noteText = item.__recallDraft === true ?
        String(item.notes || "").trim() :
        getLatestNoteText(item);
    const noteTitle = String(convertIdsToNamesForEditing(noteText) || noteText).trim();
    const noteSnippet = noteText.length > 96 ?
        `${noteText.slice(0, 96).trim()}...` :
        noteText;

    if (item.__recallDraft === true) {
        return noteText ? `
            <span
                class="block w-full min-w-0 truncate text-xs text-gray-700"
                title="${attrValue(noteTitle)}"
            >${formatAndSanitizeUserContent(noteSnippet)}</span>
        ` : `
            <span class="block min-h-4 w-full min-w-0" aria-hidden="true"></span>
        `;
    }

    return `
        <button
            type="button"
            class="block w-full min-w-0 max-w-full text-left transition-colors hover:text-[var(--dfc-brand-primary)] focus:outline-none focus-visible:text-[var(--dfc-brand-primary)]"
            data-recalls-open-notes="${attrValue(item.id)}"
            title="Open notes and history"
            aria-label="Open notes and history"
        >
            ${noteText ? `
                <span
                    class="block w-full min-w-0 truncate text-xs text-gray-700"
                    title="${attrValue(noteTitle)}"
                >${formatAndSanitizeUserContent(noteSnippet)}</span>
            ` : `
                <span class="block min-h-4 w-full min-w-0" aria-hidden="true"></span>
            `}
        </button>
    `;
}

function renderNameCell(item, patientName) {
    const draft = item.__recallDraft === true;
    if (draft && !patientName) {
        return `<span class="block min-h-4 w-full min-w-0" aria-hidden="true"></span>`;
    }
    return `
        <span class="block min-w-0 truncate text-xs" title="${attrValue(patientName)}">${escapeHTML(patientName)}</span>
    `;
}

function renderEditCell(item) {
    if (item.__recallDraft === true) {
        return `
            <div class="flex min-w-0 justify-end">
                <button
                    type="button"
                    class="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-transparent text-gray-700 transition-colors hover:bg-gray-200 focus:outline-none focus-visible:bg-gray-200"
                    data-recalls-draft-delete="${attrValue(item.id)}"
                    title="Delete recall draft"
                    aria-label="Delete recall draft"
                >
                    <span class="material-symbols-outlined pointer-events-none text-[17px]">delete</span>
                </button>
            </div>
        `;
    }

    const editAction = item.isClosedResolved === true ? "" : `
        <button
            type="button"
            class="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-transparent text-inherit transition-colors hover:bg-gray-100 focus:outline-none focus-visible:bg-gray-100"
            data-recalls-edit-details="${attrValue(item.id)}"
            title="Edit recall details"
            aria-label="Edit recall details"
        >
            <span class="material-symbols-outlined pointer-events-none text-[17px]">edit</span>
        </button>
    `;

    return `
        <div class="flex min-w-0 justify-end">
            ${editAction}
        </div>
    `;
}

function renderReadOnlyTextCell(value, fallback = "") {
    const label = value || fallback;
    if (!label) {
        return `<span class="block min-h-4 w-full min-w-0" aria-hidden="true"></span>`;
    }
    return `
        <span class="block min-w-0 truncate text-xs font-medium" title="${attrValue(label)}">
            ${escapeHTML(label)}
        </span>
    `;
}

function renderDraftStatusCell() {
    return `
        <span class="block min-w-0 truncate text-xs font-semibold uppercase tracking-normal text-gray-600" title="DRAFT">
            DRAFT
        </span>
    `;
}

function getProviderFilterKey(providerEntry) {
    if (!providerEntry) {return "";}
    if (providerEntry.type === "app" && providerEntry.userId) {
        return `app:${providerEntry.userId}`;
    }
    if (providerEntry.type === "legacy" && providerEntry.legacyKey) {
        return `legacy:${providerEntry.legacyKey}`;
    }
    return providerEntry.label ? `label:${providerEntry.label}` : "";
}

function isProcessed(item) {
    return Boolean(item?.email_date || item?.processedAt || item?.processedBy);
}

function getTimestampMillis(value) {
    if (!value) {return null;}
    if (typeof value?.toMillis === "function") {return value.toMillis();}
    if (typeof value?.seconds === "number") {
        const nanos = typeof value?.nanoseconds === "number" ?
            value.nanoseconds :
            0;
        return (value.seconds * 1000) + Math.floor(nanos / 1000000);
    }
    const parsed = new Date(value);
    const time = parsed.getTime();
    return Number.isNaN(time) ? null : time;
}

function getSortValue(item, field) {
    switch (field) {
        case "patient":
            return getPatientName(item).toLowerCase();
        case "reason":
            return getReasonSortParts(item.recall_reason);
        case "provider":
            return getProviderDisplay(item.provider).toLowerCase();
        case "status":
            return getStatusSortParts(item.status);
        case "processed":
            return item.email_date || "";
        case "closed_at_recent": {
            const closedAt = getTimestampMillis(item.closedAt);
            return item.isClosedResolved === true && closedAt !== null ?
                -closedAt :
                null;
        }
        case "updated_at":
            return getTimestampMillis(item.updatedAt);
        case "recall_date":
        default:
            return item.recall_date || "";
    }
}

function compareOrderedSortValues(valueA, valueB) {
    const orderDiff =
        (valueA?.order ?? SORT_ORDER_FALLBACK) -
        (valueB?.order ?? SORT_ORDER_FALLBACK);
    if (orderDiff !== 0) {return orderDiff;}
    const labelDiff = String(valueA?.label || "")
        .localeCompare(String(valueB?.label || ""));
    if (labelDiff !== 0) {return labelDiff;}
    return String(valueA?.value || "").localeCompare(String(valueB?.value || ""));
}

function compareSortValues(valueA, valueB) {
    if (
        (valueA && typeof valueA === "object") ||
        (valueB && typeof valueB === "object")
    ) {
        return compareOrderedSortValues(valueA || {}, valueB || {});
    }
    return compareNullablePrimitiveValues(valueA, valueB);
}

function compareNullablePrimitiveValues(valueA, valueB) {
    const hasA = valueA !== null && valueA !== undefined && valueA !== "";
    const hasB = valueB !== null && valueB !== undefined && valueB !== "";
    if (!hasA && !hasB) {return 0;}
    if (!hasA) {return 1;}
    if (!hasB) {return -1;}
    if (typeof valueA === "number" && typeof valueB === "number") {
        return valueA - valueB;
    }
    return String(valueA).localeCompare(String(valueB));
}

function getSortChain(field) {
    const userField = field || DEFAULT_RECALLS_SORT_FIELD;
    return [
        userField,
        ...DEFAULT_RECALLS_SORT_CHAIN.filter((entry) => entry !== userField),
    ];
}

function getSortDirectionForField(field, primaryField, primaryDirection) {
    return field === primaryField ? primaryDirection : 1;
}

function matchesSearch(item, query) {
    if (!query) {return true;}
    const searchHaystack = [
        getPatientName(item),
        item.email,
        item.phone,
        getRecallReasonLabel(item.recall_reason),
        getProviderDisplay(item.provider),
        getStatusLabel(item.status),
        getLatestNoteText(item),
        item.notesSearchText,
        item.recall_date,
        item.email_date,
    ].join(" ").toLowerCase();
    return searchHaystack.includes(query);
}

function matchesProvider(item, providerFilter) {
    const providerFilters = getFilterValues(providerFilter);
    if (providerFilters.length === 0) {return true;}
    return (item.provider || []).some((provider) =>
        providerFilters.includes(getProviderFilterKey(provider)));
}

function filterRows(rows) {
    const filters = recallsRouteState.filters || {};
    const query = (filters.search || "").trim().toLowerCase();
    const reasonFilters = getFilterValues(filters.reason);
    const statusFilters = getFilterValues(filters.status);
    const processedFilters = getFilterValues(filters.processed);

    return rows.filter((item) => {
        if (!matchesSearch(item, query)) {return false;}
        if (
            reasonFilters.length > 0 &&
            !normalizeRecallReasonIds(item.recall_reason)
                .some((reason) => reasonFilters.includes(reason))
        ) {
            return false;
        }
        if (
            statusFilters.length > 0 &&
            !statusFilters.includes(String(item.status || ""))
        ) {
            return false;
        }
        if (!matchesProvider(item, filters.provider)) {return false;}
        if (
            processedFilters.length > 0 &&
            !processedFilters.includes(isProcessed(item) ? "processed" : "unprocessed")
        ) {
            return false;
        }
        return true;
    });
}

function sortRows(rows) {
    const sort = recallsRouteState.sort || {};
    const direction = sort.direction === "desc" ? -1 : 1;
    const field = sort.field || DEFAULT_RECALLS_SORT_FIELD;
    const sortChain = getSortChain(field);
    return [...rows].sort((a, b) => {
        for (const sortField of sortChain) {
            const aValue = getSortValue(a, sortField);
            const bValue = getSortValue(b, sortField);
            const valueDiff = compareSortValues(aValue, bValue);
            if (valueDiff !== 0) {
                return valueDiff *
                    getSortDirectionForField(sortField, field, direction);
            }
        }
        return 0;
    });
}

function groupByMonth(rows) {
    const grouped = new Map();
    rows.forEach((item) => {
        const monthKey = getEffectiveMonth(item);
        if (!grouped.has(monthKey)) {
            grouped.set(monthKey, []);
        }
        grouped.get(monthKey).push(item);
    });
    return grouped;
}

function groupClosedRowsByMonth(rows) {
    const grouped = new Map();
    rows.forEach((item) => {
        const monthKey = item?.closedMonth || getEffectiveMonth(item);
        if (!grouped.has(monthKey)) {
            grouped.set(monthKey, []);
        }
        grouped.get(monthKey).push(item);
    });
    return grouped;
}

function getReasonOptions() {
    return getAdminOrderedOptionEntries(appData.config?.recalls?.recallReasons || [])
        .filter(({ option }) => option?.id && isOptionActive(option))
        .map(({ option }) => ({ value: option.id, label: option.name || option.id }));
}

function getStatusOptions() {
    return getAdminOrderedOptionEntries(appData.config?.recallStatus || [])
        .filter(({ option }) => option?.id && isOptionActive(option))
        .map(({ option }) => ({ value: option.id, label: option.name || option.id }));
}

function getProviderOptions() {
    const providers = new Map();
    getLoadedRecallsRows()
        .flatMap((item) => item.provider || [])
        .forEach((provider) => {
            const key = getProviderFilterKey(provider);
            const label = getProviderDisplay([provider]);
            if (key && label && !providers.has(key)) {
                providers.set(key, label);
            }
        });

    return Array.from(providers.entries())
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label));
}

function getFilterControlOptions(options) {
    return options;
}

function getFilterValues(value) {
    const values = Array.isArray(value) ? value : [value];
    return [...new Set(values
        .map((entry) => String(entry || "").trim())
        .filter((entry) => entry && entry !== "all"))];
}

function getActiveFilterCount(filters = recallsRouteState.filters || {}) {
    return ["reason", "provider", "status", "processed"].reduce(
        (count, key) => count + getFilterValues(filters[key]).length,
        0,
    );
}

function getSortLabel(sort = recallsRouteState.sort || {}) {
    const option = SORT_OPTIONS.find((entry) => entry.value === sort.field);
    const direction = sort.direction === "desc" ? "descending" : "ascending";
    return `${option?.label || "Status"}, ${direction}`;
}

function isSortActive(sort = recallsRouteState.sort || {}) {
    return Boolean(
        sort.field &&
        (
            sort.field !== DEFAULT_RECALLS_SORT_FIELD ||
            (sort.direction || DEFAULT_RECALLS_SORT_DIRECTION) !==
                DEFAULT_RECALLS_SORT_DIRECTION
        ),
    );
}

function renderRecallsControlButton({
    active = false,
    badgeCount = 0,
    icon,
    label,
    pressed = null,
    trigger = "",
}) {
    const stateClass = active ?
        RECALLS_CONTROL_BUTTON_ACTIVE_CLASS :
        RECALLS_CONTROL_BUTTON_INACTIVE_CLASS;
    const pressedAttr = pressed === null ? "" : `aria-pressed="${pressed ? "true" : "false"}"`;

    return `
        <button
            type="button"
            class="${RECALLS_CONTROL_BUTTON_CLASS} ${stateClass}"
            ${trigger}
            ${pressedAttr}
            title="${attrValue(label)}"
            aria-label="${attrValue(label)}"
        >
            <span class="material-symbols-outlined pointer-events-none text-[20px]">${icon}</span>
            ${badgeCount > 0 ? `
                <span class="pointer-events-none absolute -right-1 -top-1 inline-flex min-w-[18px] items-center justify-center rounded-full bg-[var(--dfc-brand-accent)] px-1.5 text-[10px] font-semibold text-white">
                    ${badgeCount}
                </span>
            ` : ""}
        </button>
    `;
}

function getControlMenuWidth(menuId) {
    return menuId === "filter" ? 256 : 240;
}

function renderControlMenuShell({
    active = false,
    badgeCount = 0,
    clearable = false,
    icon,
    label,
    menuHtml,
    menuId,
}) {
    const stateClass = active ?
        RECALLS_CONTROL_BUTTON_ACTIVE_CLASS :
        RECALLS_CONTROL_BUTTON_INACTIVE_CLASS;
    const isOpen = recallsRouteState.openControlMenu === menuId;

    return `
        <div
            class="relative"
            data-recalls-control-wrapper="${attrValue(menuId)}"
        >
            <button
                type="button"
                class="${RECALLS_CONTROL_BUTTON_CLASS} ${stateClass}"
                title="${attrValue(label)}"
                aria-label="${attrValue(label)}"
                aria-expanded="${isOpen ? "true" : "false"}"
                data-recalls-control-summary
                data-recalls-control-trigger="${attrValue(menuId)}"
            >
                <span class="material-symbols-outlined pointer-events-none text-[20px]">${icon}</span>
            </button>
            ${clearable && active ? `
                <button
                    type="button"
                    class="absolute -bottom-1 -right-1 inline-flex h-5 w-5 items-center justify-center rounded-full border border-transparent bg-white text-[11px] leading-none text-gray-400 transition-colors hover:bg-[var(--dfc-brand-primary)] hover:text-[var(--dfc-brand-primary-contrast)] focus:outline-none"
                    data-recalls-control-clear="${attrValue(menuId)}"
                    aria-label="Clear ${attrValue(menuId)}"
                >
                    <span class="pointer-events-none">x</span>
                </button>
            ` : ""}
            ${badgeCount > 0 ? `
                <span class="pointer-events-none absolute -right-1 -top-1 inline-flex min-w-[18px] items-center justify-center rounded-full bg-[var(--dfc-brand-accent)] px-1.5 text-[10px] font-semibold text-white">
                    ${badgeCount}
                </span>
            ` : ""}
            <div
                class="${RECALLS_CONTROL_MENU_CLASS}"
                data-recalls-control-menu
                ${isOpen ? "" : "hidden"}
                style="width: ${getControlMenuWidth(menuId)}px;"
            >
                ${menuHtml}
            </div>
        </div>
    `;
}

function renderMenuHeading(title) {
    return `
        <div class="mb-2 text-sm font-semibold text-gray-500">${escapeHTML(title)}</div>
    `;
}

function getMenuRowStateClass(active) {
    return active ?
        "text-[var(--dfc-brand-accent)]" :
        "text-gray-700 hover:bg-gray-50";
}

function renderCountBadge(count, label) {
    if (count <= 0) {return "";}
    return `
        <span
            class="inline-flex min-w-[18px] items-center justify-center rounded-full bg-[var(--dfc-brand-accent)] px-1.5 text-[10px] font-semibold text-white"
            aria-label="${attrValue(label)}"
        >
            ${count}
        </span>
    `;
}

function renderVisualCheckbox({ checked, filterKey, value }) {
    return `
        <span class="relative inline-flex h-4 w-4 shrink-0 items-center justify-center">
            <input
                type="checkbox"
                class="peer sr-only"
                value="${attrValue(value)}"
                data-recalls-filter-option="${attrValue(filterKey)}"
                data-recalls-filter-value="${attrValue(value)}"
                ${checked ? "checked" : ""}
            >
            <span
                class="inline-flex h-4 w-4 items-center justify-center rounded border transition-colors peer-focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]"
                style="background-color: ${checked ? "var(--dfc-brand-accent)" : "#ffffff"}; border-color: ${checked ? "var(--dfc-brand-accent)" : "#d1d5db"}; color: ${checked ? "#ffffff" : "transparent"};"
                aria-hidden="true"
            >
                <span class="material-symbols-outlined text-[13px] leading-none">check</span>
            </span>
        </span>
    `;
}

function getRecallsFilterDefinitions() {
    return [
        { key: "reason", label: "Reason", options: getReasonOptions() },
        { key: "provider", label: "Provider", options: getProviderOptions() },
        { key: "status", label: "Status", options: getStatusOptions() },
        {
            key: "processed",
            label: "Emailed",
            options: PROCESSED_FILTERS.filter((option) =>
                option.value !== "all"),
        },
    ];
}

function getFilterDefinition(filterKey) {
    return getRecallsFilterDefinitions()
        .find((definition) => definition.key === filterKey) || null;
}

function getSingleFilterCount(value) {
    return getFilterValues(value).length;
}

function getFilterComparableValues(item, filterKey) {
    switch (filterKey) {
        case "reason":
            return normalizeRecallReasonIds(item.recall_reason);
        case "provider":
            return (item.provider || [])
                .map((provider) => getProviderFilterKey(provider))
                .filter(Boolean);
        case "status":
            return item.status ? [item.status] : [];
        case "processed":
            return [isProcessed(item) ? "processed" : "unprocessed"];
        default:
            return [];
    }
}

function itemMatchesFilter(item, filterKey, filterValue) {
    const filterValues = getFilterValues(filterValue);
    if (filterValues.length === 0) {return true;}
    return getFilterComparableValues(item, filterKey)
        .some((value) => filterValues.includes(String(value)));
}

function getRowsForFilterCounts(omittedFilterKey) {
    const filters = recallsRouteState.filters || {};
    const query = (filters.search || "").trim().toLowerCase();
    const filterKeys = ["reason", "provider", "status", "processed"];
    return getLoadedRecallsRows().filter((item) => {
        if (!matchesSearch(item, query)) {return false;}
        return filterKeys.every((filterKey) =>
            filterKey === omittedFilterKey ||
            itemMatchesFilter(item, filterKey, filters[filterKey]));
    });
}

function getRecallsFilterOptionCounts(filterKey) {
    const counts = new Map();
    getRowsForFilterCounts(filterKey).forEach((item) => {
        const values = [...new Set(getFilterComparableValues(item, filterKey))];
        values.forEach((value) => {
            counts.set(value, (counts.get(value) || 0) + 1);
        });
    });
    return counts;
}

function renderFilterCategoryMenu(filters) {
    const totalActiveCount = getActiveFilterCount(filters);
    return `
        <div class="mb-2 flex items-center justify-between gap-3">
            <div class="text-sm font-semibold text-gray-500">Filter</div>
            ${renderCountBadge(totalActiveCount, `${totalActiveCount} active filters`)}
        </div>
        <div class="space-y-1">
            ${getRecallsFilterDefinitions().map((definition) => {
                const activeCount = getSingleFilterCount(filters[definition.key]);
                return `
                    <button
                        type="button"
                        class="flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                        data-recalls-filter-category="${attrValue(definition.key)}"
                    >
                        <span>${escapeHTML(definition.label)}</span>
                        ${activeCount > 0 ? `<span class="text-xs font-semibold text-[var(--dfc-brand-accent)]">${activeCount}</span>` : ""}
                    </button>
                `;
            }).join("")}
        </div>
        ${totalActiveCount > 0 ? `
            <button
                type="button"
                class="mt-3 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-600 transition-colors hover:border-[var(--dfc-brand-primary)] hover:text-[var(--dfc-brand-primary)] focus:outline-none"
                data-recalls-clear-filters
            >
                Clear Filters
            </button>
        ` : ""}
    `;
}

function renderRecallsSwitch({ checked, inputAttrs, label }) {
    return `
        <div class="flex items-center gap-2">
            <span class="whitespace-nowrap text-sm font-medium text-gray-700">${escapeHTML(label)}</span>
            <label class="board-footer-switch shrink-0">
                <input
                    type="checkbox"
                    aria-label="${escapeHTML(label)}"
                    ${inputAttrs}
                    ${checkedAttr(checked)}
                    data-native-field-skip="true"
                >
                <span class="board-footer-switch-track"></span>
            </label>
        </div>
    `;
}

function renderClosedVisibilityControls() {
    return `
        <div class="mt-6 flex flex-wrap items-center justify-end gap-x-6 gap-y-3" data-recalls-closed-toggles>
            ${renderRecallsSwitch({
                checked: recallsRouteState.showClosed,
                inputAttrs: "data-recalls-show-closed",
                label: "Show closed",
            })}
            ${recallsRouteState.showClosed ? renderRecallsSwitch({
                checked: recallsRouteState.showPreviousMonths,
                inputAttrs: "data-recalls-show-previous-months",
                label: "Show previous months",
            }) : ""}
        </div>
    `;
}

/**
 * Renders Recalls footer controls.
 * @returns {string} Footer controls HTML.
 */
export function renderRecallsFooterControls() {
    return `
        <div id="recalls-board-footer">
            ${renderClosedVisibilityControls()}
        </div>
    `;
}

function renderFilterDetailMenu(filters, filterKey) {
    const definition = getFilterDefinition(filterKey);
    if (!definition) {return renderFilterCategoryMenu(filters);}
    const currentValues = getFilterValues(filters[filterKey]);
    const options = getFilterControlOptions(definition.options);
    const selectedCount = currentValues.length;
    const optionCounts = getRecallsFilterOptionCounts(filterKey);

    return `
        <button
            type="button"
            class="mb-2 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[var(--dfc-brand-primary)] focus:outline-none"
            data-recalls-filter-back
        >
            <span class="material-symbols-outlined text-[18px]">arrow_back</span>
            <span>Filter</span>
        </button>
        <div class="mb-2 flex items-center justify-between gap-3">
            <div class="flex min-w-0 items-center gap-2">
                <div class="min-w-0 truncate text-sm font-semibold text-gray-500">${escapeHTML(definition.label)}</div>
                ${renderCountBadge(selectedCount, `${selectedCount} active ${definition.label} filters`)}
            </div>
            ${selectedCount > 0 ? `
                <button
                    type="button"
                    class="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-transparent bg-white text-xs leading-none text-gray-400 transition-colors hover:bg-[var(--dfc-brand-primary)] hover:text-[var(--dfc-brand-primary-contrast)] focus:outline-none"
                    data-recalls-filter-option="${attrValue(filterKey)}"
                    data-recalls-filter-value="all"
                    aria-label="Clear ${attrValue(definition.label)} filter"
                >
                    <span class="pointer-events-none">x</span>
                </button>
            ` : ""}
        </div>
        <div class="max-h-64 space-y-1 overflow-y-auto pr-1">
            ${options.map((option) => {
                const active = currentValues.includes(String(option.value));
                return `
                    <label class="flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50">
                        ${renderVisualCheckbox({
                            checked: active,
                            filterKey,
                            value: option.value,
                        })}
                        <span class="min-w-0 flex-1 truncate">${escapeHTML(option.label)}</span>
                        <span class="shrink-0 text-xs text-gray-400">(${optionCounts.get(String(option.value)) || 0})</span>
                    </label>
                `;
            }).join("")}
        </div>
    `;
}

function renderFilterMenu(filters) {
    return recallsRouteState.openFilterKey ?
        renderFilterDetailMenu(filters, recallsRouteState.openFilterKey) :
        renderFilterCategoryMenu(filters);
}

function renderSortMenu(sort) {
    const field = sort.field || DEFAULT_RECALLS_SORT_FIELD;
    const direction = sort.direction === "desc" ? "desc" : "asc";
    const directionGlyph = direction === "desc" ? "▼" : "▲";
    return `
        ${renderMenuHeading("Sort")}
        <div class="space-y-1">
            ${SORT_OPTIONS.map((option) => {
                const active = field === option.value;
                return `
                    <button
                        type="button"
                        class="${MENU_ROW_CLASSES} ${getMenuRowStateClass(active)}"
                        data-recalls-sort-option="${attrValue(option.value)}"
                    >
                        <span class="inline-flex min-w-0 items-center gap-1.5">
                            <span class="truncate">${escapeHTML(option.label)}</span>
                            ${active ? `<span class="text-[11px] leading-none text-[var(--dfc-brand-accent)]">${directionGlyph}</span>` : ""}
                        </span>
                    </button>
                `;
            }).join("")}
        </div>
        <button
            type="button"
            class="mt-3 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-600 transition-colors hover:border-[var(--dfc-brand-primary)] hover:text-[var(--dfc-brand-primary)] focus:outline-none"
            data-recalls-sort-clear
        >
            Clear Sort
        </button>
    `;
}

function renderExportControls() {
    const exportModeActive = recallsRouteState.exportSelection?.active === true;

    return `
        <div class="flex items-center gap-2" data-recalls-export-controls>
            ${renderRecallsControlButton({
                active: exportModeActive,
                icon: "download",
                label: exportModeActive ? "Stop selecting recalls for export" : "Select recalls for export",
                pressed: exportModeActive,
                trigger: "data-recalls-export-toggle",
            })}
        </div>
    `;
}

function renderAdvancedSearchControl() {
    const active = recallsRouteState.advancedSearch?.active === true;
    const stateClass = active ?
        RECALLS_CONTROL_BUTTON_ACTIVE_CLASS :
        RECALLS_CONTROL_BUTTON_INACTIVE_CLASS;
    const label = active ?
        "Review advanced search" :
        "Advanced search";

    return `
        <div class="relative" data-recalls-advanced-search-wrapper>
            <button
                type="button"
                class="${RECALLS_CONTROL_BUTTON_CLASS} ${stateClass}"
                data-recalls-advanced-search-open
                title="${attrValue(label)}"
                aria-label="${attrValue(label)}"
                aria-pressed="${active ? "true" : "false"}"
            >
                <span class="material-symbols-outlined pointer-events-none text-[20px]">group_search</span>
            </button>
            ${active ? `
                <button
                    type="button"
                    class="absolute -bottom-1 -right-1 inline-flex h-5 w-5 items-center justify-center rounded-full border border-transparent bg-white text-[11px] leading-none text-gray-400 transition-colors hover:bg-[var(--dfc-brand-primary)] hover:text-[var(--dfc-brand-primary-contrast)] focus:outline-none"
                    data-recalls-advanced-search-clear
                    aria-label="Clear advanced search"
                >
                    <span class="pointer-events-none">x</span>
                </button>
            ` : ""}
        </div>
    `;
}

function renderExportConfirmButton() {
    const exportModeActive = recallsRouteState.exportSelection?.active === true;
    if (!exportModeActive) {return "";}

    const selectedCount = getVisibleSelectedRecallsExportIds().length;
    const isDisabled = selectedCount === 0;
    const stateClass = isDisabled ?
        "cursor-not-allowed bg-gray-200 text-gray-500" :
        "bg-[var(--dfc-brand-primary)] text-[var(--dfc-brand-primary-contrast)] hover:bg-[var(--dfc-brand-primary-hover)]";

    return `
        <button
            type="button"
            class="hidden h-10 shrink-0 items-center gap-2 rounded-full px-3 text-sm font-semibold shadow-sm transition-colors md:inline-flex ${stateClass}"
            data-recalls-export-open
            ${isDisabled ? "disabled" : ""}
        >
            <span class="material-symbols-outlined text-[20px]">file_download</span>
            <span>Export (${selectedCount})</span>
        </button>
    `;
}

function renderMobileExportFooter() {
    const exportModeActive = recallsRouteState.exportSelection?.active === true;
    if (!exportModeActive) {return "";}

    const selectedCount = getVisibleSelectedRecallsExportIds().length;
    const isDisabled = selectedCount === 0;
    const stateClass = isDisabled ?
        "cursor-not-allowed bg-gray-200 text-gray-500" :
        "bg-[var(--dfc-brand-primary)] text-[var(--dfc-brand-primary-contrast)] hover:bg-[var(--dfc-brand-primary-hover)]";

    return `
        <div class="detail-mobile-action-spacer" aria-hidden="true"></div>
        <div class="detail-mobile-action-bar" data-recalls-mobile-export-footer>
            <button
                type="button"
                class="ml-auto inline-flex h-11 min-w-0 items-center justify-center gap-2 rounded-md px-4 text-sm font-semibold transition-colors focus:outline-none ${stateClass}"
                data-recalls-export-open
                ${isDisabled ? "disabled" : ""}
            >
                <span class="material-symbols-outlined text-[20px]">file_download</span>
                <span>Export (${selectedCount})</span>
            </button>
        </div>
    `;
}

/**
 * Renders Recalls board controls.
 * @returns {string} Control HTML.
 */
export function renderRecallsControls() {
    const filters = recallsRouteState.filters || {};
    const sort = recallsRouteState.sort || {};
    const activeFilterCount = getActiveFilterCount(filters);
    const searchDraft = typeof recallsRouteState.searchDraft === "string" ?
        recallsRouteState.searchDraft :
        (filters.search || "");

    return `
        <div class="board-controls-shell flex flex-wrap items-center gap-2 sm:flex-row sm:items-start sm:justify-end" data-recalls-controls>
            ${renderExportConfirmButton()}
            <div
                class="flex h-10 min-w-0 flex-1 items-center rounded-full border border-gray-200 bg-white px-3 text-gray-500 transition-colors sm:w-[17rem] sm:flex-none sm:px-4"
                data-recalls-search-wrapper
            >
                <span class="material-symbols-outlined mr-2 text-[20px] text-gray-500" data-recalls-search-icon>search</span>
                <input
                    type="text"
                    value="${attrValue(searchDraft)}"
                    placeholder="Search Recalls"
                    class="board-search-input min-w-0 flex-1 truncate bg-transparent text-sm text-gray-700 outline-none placeholder:truncate placeholder:text-gray-400"
                    data-recalls-filter="search"
                    data-native-field-skip="true"
                >
                <button
                    type="button"
                    class="${searchDraft ? "" : "hidden"} inline-flex shrink-0 items-center justify-center self-center text-gray-400 transition-colors hover:text-[var(--dfc-brand-primary)]"
                    data-recalls-clear-search
                    aria-label="Clear Recalls search"
                >
                    <span class="material-symbols-outlined block text-[18px] leading-none">close</span>
                </button>
            </div>
            <div class="order-1 ml-auto flex shrink-0 items-center justify-end gap-1.5 sm:order-none sm:ml-0 sm:flex-wrap sm:gap-2">
                ${renderAdvancedSearchControl()}
                ${renderControlMenuShell({
                    active: isSortActive(sort),
                    clearable: true,
                    icon: "swap_vert",
                    label: `Sort recalls: ${getSortLabel(sort)}`,
                    menuId: "sort",
                    menuHtml: renderSortMenu(sort),
                })}
                ${renderControlMenuShell({
                    active: activeFilterCount > 0,
                    badgeCount: activeFilterCount,
                    clearable: true,
                    icon: "filter_alt",
                    label: "Filter recalls",
                    menuId: "filter",
                    menuHtml: renderFilterMenu(filters),
                })}
                ${renderExportControls()}
                <button
                    type="button"
                    class="hidden h-10 w-10 items-center justify-center rounded-full bg-[var(--dfc-brand-primary)] text-[var(--dfc-brand-primary-contrast)] shadow-sm transition-colors hover:bg-[var(--dfc-brand-primary-hover)] md:inline-flex"
                    data-recalls-action="quick-add"
                    title="Quick Add Recall"
                    aria-label="Quick Add Recall"
                >
                    <span class="material-symbols-outlined pointer-events-none text-[22px]">add</span>
                </button>
            </div>
        </div>
    `;
}

function renderExportCell(item, exportColumnActive) {
    if (!exportColumnActive) {return "";}
    const selected = getSelectedRecallsExportIds().includes(item.id);
    if (isRecallExportSelectable(item)) {
        return `
            <td class="px-1.5 py-1.5 align-middle">
                ${renderCheckboxControl({
                    ariaLabel: "Select recall for export",
                    checked: selected,
                    inputAttrs: `data-recalls-export-row="${attrValue(item.id)}"`,
                    labelClassName: "w-full justify-center",
                })}
            </td>
        `;
    }
    return `
        <td class="px-1.5 py-1.5 align-middle"></td>
    `;
}

function renderTableColumnGroup(exportModeActive) {
    return `
        <colgroup>
            ${exportModeActive ? `<col class="recalls-col-export">` : ""}
            <col class="recalls-col-name">
            <col class="recalls-col-email">
            <col class="recalls-col-phone">
            <col class="recalls-col-reason">
            <col class="recalls-col-provider">
            <col class="recalls-col-date">
            <col class="recalls-col-status">
            <col class="recalls-col-notes">
            <col class="recalls-col-edit">
        </colgroup>
    `;
}

function renderRow(item, { exportColumnActive = false } = {}) {
    const draft = item.__recallDraft === true;
    const closed = item.isClosedResolved === true;
    const statusRow = draft ? { className: "", style: "" } :
        getStatusRowPresentation(item.status);
    const rowClass = [
        "group/recall-row border-b border-gray-100 last:border-0 transition-colors",
        draft ? "recalls-draft-table-row cursor-pointer" : "",
        draft ? "" : "max-md:cursor-pointer",
        statusRow.className || draft ? "" : "hover:bg-gray-50/80",
        statusRow.className,
        closed ? "bg-gray-50 text-gray-500" : "",
    ].filter(Boolean).join(" ");
    const rowStyle = statusRow.style;
    const patientName = getPatientName(item) || (draft ? "" : "Unnamed");
    const cellClass = "truncate px-2.5 py-1.5 align-middle";
    const activeTextClass = statusRow.className ? "text-inherit" : "text-gray-700";
    const nameTextClass = statusRow.className ? "text-inherit" : "text-gray-900";
    const draftFallback = draft ? "" : null;
    const reasonFallback = draftFallback ?? "No reason";
    const providerFallback = draftFallback ?? "No provider";
    const recallDateLabel = formatDate(item.recall_date) || item.recall_date || "";

    const mobileViewAttr = !draft ?
        `data-recalls-mobile-view="${attrValue(item.id)}"` :
        "";

    return `
        <tr class="${rowClass}" style="${attrValue(rowStyle)}" data-id="${attrValue(item.id)}" data-closed="${closed ? "true" : "false"}" ${draft ? `data-recall-draft-id="${attrValue(item.id)}"` : ""} ${mobileViewAttr}>
            ${renderExportCell(item, exportColumnActive)}
            <td class="${cellClass} font-medium ${nameTextClass}">${renderNameCell(item, patientName)}</td>
            <td class="${cellClass} ${activeTextClass}">${renderReadOnlyTextCell(item.email || "")}</td>
            <td class="${cellClass} ${activeTextClass}">${renderReadOnlyTextCell(item.phone || "")}</td>
            <td class="${cellClass} ${activeTextClass}">${renderReadOnlyTextCell(getRecallReasonLabel(item.recall_reason), reasonFallback)}</td>
            <td class="${cellClass} ${activeTextClass}">${renderReadOnlyTextCell(getProviderDisplay(item.provider), providerFallback)}</td>
            <td class="${cellClass} ${activeTextClass}">${renderReadOnlyTextCell(recallDateLabel)}</td>
            <td class="${cellClass} ${activeTextClass}">${draft ? renderDraftStatusCell() : renderRecallStatusInlineControl(item)}</td>
            <td class="px-2.5 py-1.5 ${activeTextClass}">${renderLatestNoteCell(item)}</td>
            <td class="px-1 py-1.5 text-right align-middle lg:px-2">${renderEditCell(item)}</td>
        </tr>
    `;
}

function renderRowsTable(
    rows,
    { emptyMessage = "No recalls in this month." } = {},
) {
    const exportColumnActive =
        recallsRouteState.exportSelection?.active === true &&
        rows.some(isRecallExportSelectable);
    const exportHeader = exportColumnActive ?
        `<th class="px-1.5 py-2"><span class="sr-only">Select recalls</span></th>` :
        "";
    const columnCount = TABLE_COLUMN_COUNT +
        (exportColumnActive ? 1 : 0);
    const rowsHtml = rows.length > 0 ?
        rows.map((row) => renderRow(row, { exportColumnActive })).join("") :
        `
            <tr>
                <td class="px-3 py-4 text-sm text-gray-500" colspan="${columnCount}">
                    ${escapeHTML(emptyMessage)}
                </td>
            </tr>
        `;
    return `
        <div class="overflow-x-auto lg:overflow-x-visible">
            <table class="recalls-rows-table ${exportColumnActive ? "recalls-rows-table--export" : ""} w-full min-w-[80rem] table-fixed text-left text-sm text-gray-600 lg:min-w-0">
                ${renderTableColumnGroup(exportColumnActive)}
                <thead class="border-y border-slate-100 bg-gray-50 text-xs uppercase text-gray-600">
                    <tr>
                        ${exportHeader}
                        <th class="px-2.5 py-2 font-semibold">Name</th>
                        <th class="px-2.5 py-2 font-semibold">Email</th>
                        <th class="px-2.5 py-2 font-semibold">Phone</th>
                        <th class="px-2.5 py-2 font-semibold">Reason</th>
                        <th class="px-2.5 py-2 font-semibold">Provider</th>
                        <th class="px-2.5 py-2 font-semibold">Recall date</th>
                        <th class="px-2.5 py-2 font-semibold">Status</th>
                        <th class="px-2.5 py-2 font-semibold">Notes</th>
                        <th class="px-1 py-2 lg:px-2"><span class="sr-only">Edit</span></th>
                    </tr>
                </thead>
                <tbody>${rowsHtml}</tbody>
            </table>
        </div>
    `;
}

function renderTableStateRows({ loading = false, message }) {
    if (!loading) {
        return `
            <tr>
                <td class="px-3 py-4 text-sm text-gray-500" colspan="${TABLE_COLUMN_COUNT}">
                    ${escapeHTML(message)}
                </td>
            </tr>
        `;
    }

    return Array.from({ length: 3 }).map(() => `
        <tr>
            ${Array.from({ length: TABLE_COLUMN_COUNT }).map(() => `
                <td class="px-3 py-3">
                    <span class="block h-3 w-full max-w-28 animate-pulse rounded bg-gray-100"></span>
                </td>
            `).join("")}
        </tr>
    `).join("");
}

/**
 * Renders a compact Recalls table-state section.
 * @param {object} options State rendering options.
 * @param {boolean} [options.loading=false] Whether to render skeleton rows.
 * @param {string} options.message Muted state message.
 * @param {string} [options.actionHtml=""] Optional action HTML.
 * @param {string} [options.title] State title.
 * @param {"neutral"|"danger"} [options.tone="neutral"] State tone.
 * @returns {string} Table-state HTML.
 */
export function renderRecallsTableState({
    actionHtml = "",
    loading = false,
    message,
    title = "",
    tone = "neutral",
}) {
    const toneClasses = tone === "danger" ?
        "border-red-200 bg-red-50 text-red-800" :
        "border-gray-200 bg-white text-gray-600";
    const headerLabel = title ||
        (loading ? "Loading recalls" :
            (tone === "danger" ? "Recalls unavailable" : "No matching recalls"));
    const action = !loading && actionHtml ? `
        <div class="px-3 pb-4">
            ${actionHtml}
        </div>
    ` : "";

    return `
        <section class="rounded-lg border shadow-sm ${toneClasses}">
            <div class="flex items-center justify-between gap-3 border-b border-inherit px-4 py-3">
                <span class="text-sm font-semibold">${headerLabel}</span>
                ${loading ? `
                    <span class="text-xs text-gray-500">Updating table</span>
                ` : ""}
            </div>
            <div class="overflow-x-auto">
                <table class="min-w-[80rem] text-left text-sm">
                    <tbody>
                        ${renderTableStateRows({ loading, message })}
                    </tbody>
                </table>
            </div>
            ${action}
        </section>
    `;
}

function hasActiveRecallsFilters() {
    const filters = recallsRouteState.filters || {};
    return Boolean(
        (filters.search || "").trim() ||
        getActiveFilterCount(filters) > 0,
    );
}

function renderPermanentQueueEmptyState() {
    const activeFilters = hasActiveRecallsFilters();
    const message = activeFilters ?
        "No recalls match the current filters." :
        "No recalls are loaded for the current view.";
    return renderRecallsTableState({
        message,
        title: activeFilters ?
            "No recalls match filters" :
            "No recalls",
    });
}

/**
 * Renders shared Recall drafts using the same columns as permanent Recalls.
 * @returns {string} Draft table section HTML.
 */
export function renderRecallsDraftsTableSection() {
    const drafts = (appData.recallDrafts || [])
        .map((draft) => ({
            ...draft,
            __recallDraft: true,
        }));
    if (drafts.length === 0) {return "";}
    const expanded = recallsRouteState.draftsCollapsed !== true;

    return `
        <section class="overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm" data-recalls-drafts-section>
            <div class="flex w-full items-center justify-between gap-3 border-b border-gray-100 bg-white px-3 py-2">
                <div class="flex min-w-0 items-center gap-2">
                    <h3 class="text-sm font-semibold text-gray-900">Draft Recalls</h3>
                </div>
                <button
                    type="button"
                    class="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-white hover:text-gray-700"
                    data-recalls-toggle-drafts
                    aria-label="${expanded ? "Collapse" : "Expand"} Draft Recalls"
                    aria-expanded="${expanded ? "true" : "false"}"
                >
                    <span class="material-symbols-outlined text-[19px]">
                        ${expanded ? "expand_less" : "expand_more"}
                    </span>
                </button>
            </div>
            ${expanded ? renderRowsTable(drafts, {
                emptyMessage: "No draft recalls.",
            }) : ""}
        </section>
    `;
}

function isOperationalMonthLoaded(monthKey) {
    const currentMonth = getMonthKey();
    const nextMonth = addMonths(currentMonth, 1);
    return isValidMonthKey(monthKey) &&
        monthKey >= currentMonth &&
        monthKey <= nextMonth;
}

function isMonthLoaded(monthKey) {
    return isOperationalMonthLoaded(monthKey) ||
        (recallsRouteState.loadedMonthKeys || []).includes(monthKey);
}

function renderMonthBody({ error = null, loaded = false, loading = false, rows }) {
    if (loading) {
        return `
            <div class="border-t border-gray-100 px-3 py-4 text-sm text-gray-500">
                Loading recalls...
            </div>
        `;
    }
    if (error) {
        return `
            <div class="border-t border-red-100 px-3 py-4 text-sm text-red-700">
                Recalls could not be loaded for this month.
            </div>
        `;
    }
    if (!loaded && rows.length === 0) {
        return "";
    }
    return renderRowsTable(rows, {
        emptyMessage: hasActiveRecallsFilters() ?
            "No recalls match the current filters." :
            "No recalls in this month.",
    });
}

function renderMonthSection({
    hasActiveRows = false,
    loaded = false,
    monthKey,
    prefetchClosed = false,
    rows,
}) {
    const expanded = shouldMonthRenderExpanded(monthKey, { hasActiveRows });
    const activeCount = rows.filter((row) => row.isClosedResolved !== true).length;
    const closedCount = rows.length - activeCount;
    const loading = recallsRouteState.monthLoadingState?.[monthKey] === true;
    const error = recallsRouteState.monthLoadErrors?.[monthKey] || null;
    const exportModeActive = recallsRouteState.exportSelection?.active === true;
    const selectedIds = new Set(getSelectedRecallsExportIds());
    const selectableIds = rows
        .filter(isRecallExportSelectable)
        .map((row) => row.id);
    const selectedCount = selectableIds
        .filter((recallId) => selectedIds.has(recallId))
        .length;
    const monthChecked = selectableIds.length > 0 &&
        selectedCount === selectableIds.length;
    const showMonthExportControl =
        exportModeActive && selectableIds.length > 0;
    const monthHeaderPaddingClass = showMonthExportControl ?
        "py-2 pl-1.5 pr-3" :
        "px-3 py-2";
    const monthSummary = loaded ? `
        <span class="whitespace-nowrap text-xs text-gray-500 tabular-nums">
            ${activeCount} open${closedCount ? `, ${closedCount} closed` : ""}
            ${exportModeActive && selectedCount > 0 ? `, ${selectedCount} selected` : ""}
        </span>
    ` : "";

    return `
        <section
            class="overflow-hidden rounded-md border border-gray-200 bg-white"
            data-recalls-month-section="${attrValue(monthKey)}"
            data-recalls-prefetch-closed="${prefetchClosed ? "true" : "false"}"
        >
            <div class="flex w-full items-center justify-between gap-3 border-b border-gray-100 bg-white ${monthHeaderPaddingClass}">
                <div class="flex min-w-0 items-center gap-2">
                    ${showMonthExportControl ? `
                        ${renderCheckboxControl({
                            ariaLabel: `Select all exportable recalls in ${getMonthLabel(monthKey)}`,
                            checked: monthChecked,
                            inputAttrs: `
                                data-recalls-export-month="${attrValue(monthKey)}"
                                data-recalls-export-month-ids="${attrValue(selectableIds.join(","))}"
                                data-recalls-export-indeterminate="${selectedCount > 0 && !monthChecked ? "true" : "false"}"
                            `,
                            labelClassName: "shrink-0",
                        })}
                    ` : ""}
                    <span class="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5 text-left">
                        <span class="truncate text-sm font-semibold text-gray-900">${escapeHTML(getMonthLabel(monthKey))}</span>
                        ${monthSummary}
                    </span>
                </div>
                <button
                    type="button"
                    class="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-white hover:text-gray-700"
                    data-recalls-toggle-month="${attrValue(monthKey)}"
                    aria-label="${expanded ? "Collapse" : "Expand"} ${attrValue(getMonthLabel(monthKey))}"
                    aria-expanded="${expanded ? "true" : "false"}"
                >
                    <span class="material-symbols-outlined text-[19px]">
                        ${expanded ? "expand_less" : "expand_more"}
                    </span>
                </button>
            </div>
            ${expanded ? renderMonthBody({ error, loaded, loading, rows }) : ""}
        </section>
    `;
}

function renderYearHeading({ expanded, year }) {
    return `
        <div class="pt-2" data-recalls-year-heading="${attrValue(year)}">
            <button
                type="button"
                class="inline-flex items-center gap-1 text-sm font-semibold text-black"
                data-recalls-toggle-year="${attrValue(year)}"
                aria-expanded="${expanded ? "true" : "false"}"
                aria-label="${expanded ? "Collapse" : "Expand"} ${attrValue(year)}"
            >
                <span>${escapeHTML(year)}</span>
                <span class="material-symbols-outlined text-[19px] leading-none">
                    ${expanded ? "expand_less" : "expand_more"}
                </span>
            </button>
        </div>
    `;
}

function buildMonthSections() {
    const rawActiveRows = appData.recalls || [];
    const rawClosedRows = recallsRouteState.showClosed ?
        (appData.closedRecalls || []) :
        [];
    const activeRows = filterRows(rawActiveRows);
    const closedRows = filterRows(rawClosedRows);
    const activeGroups = groupByMonth(activeRows);
    const closedGroups = groupClosedRowsByMonth(closedRows);
    const rawActiveGroups = groupByMonth(rawActiveRows);
    const rawClosedGroups = groupClosedRowsByMonth(rawClosedRows);
    const model = buildRecallsMonthBucketModel({
        activeMonthKeys: Array.from(rawActiveGroups.keys()),
        closedMonthKeys: Array.from(rawClosedGroups.keys()),
        collapsedYearHeadings: recallsRouteState.collapsedYearHeadings || [],
        expandedYearHeadings: recallsRouteState.expandedYearHeadings || [],
        latestBoundary: recallsRouteState.latestKnownMonthBoundary,
        loadedMonthKeys: recallsRouteState.loadedMonthKeys || [],
        oldestBoundary: recallsRouteState.oldestKnownMonthBoundary,
        showClosed: recallsRouteState.showClosed,
        showPreviousMonths: recallsRouteState.showPreviousMonths,
    });

    return { activeGroups, closedGroups, model, rawActiveGroups };
}

function getRenderedMonthRows() {
    if (recallsRouteState.advancedSearch?.active === true) {
        return getAdvancedSearchRenderedRows();
    }

    const {
        activeGroups,
        closedGroups,
        model,
    } = buildMonthSections();

    return model.years.flatMap((yearGroup) =>
        (yearGroup.expanded ? yearGroup.months : []).flatMap((monthKey) => [
            ...(activeGroups.get(monthKey) || []),
            ...(closedGroups.get(monthKey) || []),
        ]));
}

/**
 * Returns exportable Recall ids in the current loaded and visible result set.
 * @returns {Array<string>} Exportable visible Recall ids.
 */
export function getVisibleRecallsExportIds() {
    return getRenderedMonthRows()
        .filter(isRecallExportSelectable)
        .map((row) => row.id)
        .filter(Boolean);
}

function getVisibleSelectedRecallsExportIds() {
    const visibleIds = new Set(getVisibleRecallsExportIds());
    return getSelectedRecallsExportIds()
        .filter((recallId) => visibleIds.has(recallId));
}

function getAdvancedSearchRenderedRows() {
    return sortRows(filterRows(recallsRouteState.advancedSearch?.results || []));
}

function renderAdvancedSearchResults() {
    const searchState = recallsRouteState.advancedSearch || {};
    const rows = getAdvancedSearchRenderedRows();
    const reachedCap = searchState.reachedCap === true;
    const cap = Number(searchState.cap) || 250;
    const exportModeActive = recallsRouteState.exportSelection?.active === true;
    const selectedIds = new Set(getSelectedRecallsExportIds());
    const selectableIds = rows
        .filter(isRecallExportSelectable)
        .map((row) => row.id);
    const selectedCount = selectableIds
        .filter((recallId) => selectedIds.has(recallId))
        .length;
    const allChecked = selectableIds.length > 0 &&
        selectedCount === selectableIds.length;
    const total = Number.isFinite(Number(searchState.total)) ?
        Number(searchState.total) :
        (searchState.results || []).length;

    return `
        <div class="space-y-2">
            <section class="overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm" data-recalls-advanced-results>
                <div class="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-3 py-2">
                    <div class="flex min-w-0 items-center gap-2">
                        ${exportModeActive && selectableIds.length > 0 ? `
                            ${renderCheckboxControl({
                                ariaLabel: "Select all exportable advanced search results",
                                checked: allChecked,
                                inputAttrs: `
                                    data-recalls-export-advanced
                                    data-recalls-export-advanced-ids="${attrValue(selectableIds.join(","))}"
                                    data-recalls-export-indeterminate="${selectedCount > 0 && !allChecked ? "true" : "false"}"
                                `,
                                labelClassName: "shrink-0",
                            })}
                        ` : ""}
                        <div class="min-w-0">
                            <h3 class="text-sm font-semibold text-gray-900">Advanced Search Results</h3>
                            <p class="text-xs text-gray-500">
                                ${total} result${total === 1 ? "" : "s"}
                                ${exportModeActive && selectedCount > 0 ? `, ${selectedCount} selected` : ""}
                            </p>
                        </div>
                    </div>
                    ${reachedCap ? `
                        <div class="rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800">
                            Showing up to ${cap} results. Refine the search to narrow the list.
                        </div>
                    ` : ""}
                </div>
                ${renderRowsTable(rows, {
                    emptyMessage: hasActiveRecallsFilters() ?
                        "No recalls match the current filters." :
                        "No recalls match this search.",
                })}
            </section>
            ${renderMobileExportFooter()}
        </div>
    `;
}

/**
 * Renders filtered and grouped Recalls month sections.
 * @returns {string} Table HTML.
 */
export function renderRecallsTable() {
    if (recallsRouteState.advancedSearch?.active === true) {
        return renderAdvancedSearchResults();
    }

    const {
        activeGroups,
        closedGroups,
        model,
        rawActiveGroups,
    } = buildMonthSections();
    const currentMonth = getMonthKey();
    const nextMonth = addMonths(currentMonth, 1);

    const renderedYears = model.years.map((yearGroup) => {
        const monthSections = yearGroup.expanded ?
            yearGroup.months.map((monthKey) => {
                const rows = sortRows([
                    ...(activeGroups.get(monthKey) || []),
                    ...(closedGroups.get(monthKey) || []),
                ]);
                const hasActiveRows =
                    (rawActiveGroups.get(monthKey) || []).length > 0;
                const isDefaultVisibleMonth =
                    monthKey === currentMonth ||
                    monthKey === nextMonth ||
                    hasActiveRows;
                const loaded = isMonthLoaded(monthKey) || hasActiveRows;
                return renderMonthSection({
                    hasActiveRows,
                    loaded,
                    monthKey,
                    prefetchClosed: isDefaultVisibleMonth || loaded,
                    rows,
                });
            }).join("") :
            "";
        return `
            ${renderYearHeading(yearGroup)}
            ${yearGroup.expanded ? `<div class="space-y-2">${monthSections}</div>` : ""}
        `;
    });
    const hasRows = model.years.length > 0;

    return `
        <div class="space-y-2">
            ${hasRows ? renderedYears.join("") : renderPermanentQueueEmptyState()}
            ${renderMobileExportFooter()}
        </div>
    `;
}
